/**
 * @typedef {import('../types').RosterProvider} RosterProvider
 */

/**
 * @typedef {Object} SortOption
 * @property {string} label Display text for the option.
 * @property {string} value Unique identifier for the sort option.
 * @property {string} [columnKey] Column identifier for table header sorting.
 * @property {(providers: RosterProvider[]) => RosterProvider[]} sortFn Function to sort or filter providers.
 */

/**
 * Configuration for all sorting and filtering options on the provider roster.
 * To add a new sort, add a `SortOption` object to this array.
 * @type {SortOption[]}
 */
export const SORT_OPTIONS = [
  {
    label: 'Provider Name A - Z',
    value: 'provider-name-asc',
    columnKey: 'provider-name',
    sortFn: (providers) =>
      [...providers].sort((a, b) =>
        (a.displayName || '').localeCompare(b.displayName || ''),
      ),
  },
  {
    label: 'Provider Name Z - A',
    value: 'provider-name-desc',
    columnKey: 'provider-name',
    sortFn: (providers) =>
      [...providers].sort((a, b) =>
        (b.displayName || '').localeCompare(a.displayName || ''),
      ),
  },
  {
    label: 'Practice A - Z',
    value: 'practice-asc',
    columnKey: 'practice',
    sortFn: (providers) =>
      [...providers].sort((a, b) =>
        (a.office?.name || '').localeCompare(b.office?.name || ''),
      ),
  },
  {
    label: 'Practice Z - A',
    value: 'practice-desc',
    columnKey: 'practice',
    sortFn: (providers) =>
      [...providers].sort((a, b) =>
        (b.office?.name || '').localeCompare(a.office?.name || ''),
      ),
  },
  {
    label: 'Plan Type - Monthly',
    value: 'plan-monthly',
    sortFn: (providers) =>
      [...providers].filter((p) => p.productCode?.includes('Monthly')),
  },
  {
    label: 'Plan Type - Annual',
    value: 'plan-annual',
    sortFn: (providers) =>
      [...providers].filter((p) => p.productCode?.includes('Annual')),
  },
];

/**
 * Retrieves a sort option by its unique value.
 * @param {string} value The sort option's value.
 * @returns {SortOption | undefined}
 */
export const getSortOption = (value) => {
  return SORT_OPTIONS.find((option) => option.value === value);
};

/**
 * Determines the next sort value when a table header is clicked, cycling through available options.
 * @param {string} columnKey The identifier for the column being sorted.
 * @param {string} currentSortValue The current sort value.
 * @returns {string} The next sort value.
 */
export const getNextSortValue = (columnKey, currentSortValue) => {
  // Find all sort options for this column
  const columnOptions = SORT_OPTIONS.filter(
    (option) => option.columnKey === columnKey,
  );

  if (columnOptions.length === 0) return '';

  // Find current option
  const currentOption = columnOptions.find(
    (option) => option.value === currentSortValue,
  );

  if (!currentOption) {
    // If not currently sorting by this column, start with the first option (usually ascending).
    return columnOptions[0].value;
  }

  // Find the index of the current option.
  const currentIndex = columnOptions.indexOf(currentOption);

  // Cycle to the next option, or loop back to the start.
  const nextIndex = (currentIndex + 1) % columnOptions.length;
  return columnOptions[nextIndex].value;
};

/**
 * Sorts a list of providers based on a given sort value.
 * @param {RosterProvider[]} providers The array of providers to sort.
 * @param {string} sortValue The identifier of the sort to apply.
 * @returns {RosterProvider[]} The sorted array of providers.
 */
export const sortProviders = (providers, sortValue) => {
  if (!sortValue) return providers;

  const sortOption = getSortOption(sortValue);
  if (!sortOption || !sortOption.sortFn) return providers;

  return sortOption.sortFn(providers);
};
